/** @jsx jsx */
import { Fragment, memo } from 'react';

import { css, jsx, SerializedStyles } from '@emotion/react';

import { Stack } from '@atlaskit/primitives';
import { UNSAFE_media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import type { TransformedTokenMerged } from '../types';
import { getTokenListColumnNames } from '../utils';

import NoResults from './no-results';
import TokenRow from './token-row';

const tableHeaderStyles = css({
  position: 'sticky',
  zIndex: 1,
  backgroundColor: token('elevation.surface', '#FFFFFF'),
});

const tableStyles = css({
  display: 'flex',
  boxSizing: 'border-box',
  justifyContent: 'stretch',
  gap: token('space.200', '16px'),
  flexDirection: 'column',
  border: 'none',
});

const tableHeaderRowStyles = css({
  display: 'flex',
  padding: `${token('space.050', '4px')} 0`,
  gap: token('space.200', '16px'),
  borderBlockEnd: `${token('border.width', '2px')} solid ${token(
    'color.border',
    '#091E4224',
  )}`,
});

const resetDefaultStyles = css({
  border: 'none',
});

const tableHeaderValueColumnStyles = css({
  minWidth: '6rem',
  padding: 0,
  [UNSAFE_media.below.sm]: {
    display: 'none',
  },
});

const tableHeaderFirstColumn = css({
  width: '80%',
  padding: 0,
});

export interface TokenListProps {
  /**
   * Offset the sticky table header to avoid overlapping existing fixed elements.
   */
  scrollOffset?: number;
  list?: TransformedTokenMerged[];
  isLoading?: boolean;
  isInSearchResult?: boolean;
  testId?: string;
  label?: string;
}

const TokenTable = ({
  list,
  scrollOffset = 0,
  isLoading,
  isInSearchResult = false,
  testId,
  label,
}: TokenListProps) => {
  if (list !== undefined && list.length === 0) {
    return <NoResults />;
  }

  const group = list && list[0].attributes.group;
  const columnNames = getTokenListColumnNames(group);

  return (
    <Stack space="space.200" testId={testId && `${testId}-token-list`}>
      <table css={tableStyles} aria-labelledby={label}>
        <thead
          css={[resetDefaultStyles, tableHeaderStyles, { top: scrollOffset }]}
        >
          <ListHeader columnNames={columnNames} />
        </thead>
        <tbody css={resetDefaultStyles}>
          {isLoading ? (
            <Fragment>
              <TokenRow key={1} isLoading />
              <TokenRow key={2} isLoading />
              <TokenRow key={3} isLoading />
              <TokenRow key={4} isLoading />
              <TokenRow key={5} isLoading />
            </Fragment>
          ) : (
            list?.map(tokenItem => (
              <TokenRow
                key={tokenItem.name}
                token={tokenItem}
                testId={testId}
                isInSearchResult={isInSearchResult}
              />
            ))
          )}
        </tbody>
      </table>
    </Stack>
  );
};

type ListHeaderProps = {
  columnNames: string[];
};

const ListHeader = ({ columnNames }: ListHeaderProps) => (
  <tr css={tableHeaderRowStyles}>
    <ColumnHeader columnName={columnNames[0]} styles={tableHeaderFirstColumn} />
    {columnNames.slice(1).map(columnName => (
      <ColumnHeader
        key={columnName}
        columnName={columnName}
        styles={tableHeaderValueColumnStyles}
      />
    ))}
  </tr>
);

interface ColumnHeaderProps {
  columnName: string;
  styles: SerializedStyles;
}

const ColumnHeader = ({ columnName, styles }: ColumnHeaderProps) => (
  <th scope="col" css={styles}>
    <strong>{columnName}</strong>
  </th>
);

export default memo(TokenTable);
